import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Loader2, CheckCircle2, ArrowRight, Star, CalendarX, BadgeCheck, Clock, ChevronDown } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { Eyebrow, Assurances } from "@/components/kit";
import { services } from "@/data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "31641262267";

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    interest: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Vul minimaal je naam, e-mail en bericht in.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      toast.success("Bericht verzonden — we reageren binnen 24 uur.");
      setForm({ name: "", email: "", phone: "", company: "", interest: "", message: "" });
      setTimeout(() => setStatus("idle"), 4000);
    } catch (err) {
      console.error(err);
      setStatus("idle");
      toast.error("Er ging iets mis. Probeer opnieuw of mail info@uppit.nl direct.");
    }
  };

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Hoi Tim, ik wil graag samenwerken met UPPIT."
  )}`;

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-ivory py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left */}
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>Contact · Start je groei</Eyebrow>
              <h2 className="mt-6 font-display font-black text-coal tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
                Laten we <span className="text-gradient-blue">beginnen.</span>
              </h2>
              <p className="mt-6 text-lg text-coal/60 leading-relaxed max-w-md">
                Plan een vrijblijvend gesprek. Binnen 30 minuten weet je waar jouw
                groeikansen liggen — geen offerte-carrousel, geen sales-script.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10 space-y-3">
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="group flex items-center gap-4 rounded-2xl border border-ivory-400 bg-white p-4 hover:border-electric/40 hover:shadow-card-light transition-all"
                >
                  <div className="w-11 h-11 rounded-xl bg-electric/10 grid place-items-center text-electric group-hover:bg-electric group-hover:text-white transition-colors shrink-0">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-bold text-coal">WhatsApp</div>
                    <div className="text-sm text-coal/55">+31 6 41 26 22 67 — snelste antwoord</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-coal/40 group-hover:text-electric group-hover:translate-x-0.5 transition-all" />
                </a>
                <a
                  href="mailto:info@uppit.nl?subject=Aanvraag%20samenwerking%20UPPIT"
                  data-testid="contact-email"
                  className="group flex items-center gap-4 rounded-2xl border border-ivory-400 bg-white p-4 hover:border-electric/40 hover:shadow-card-light transition-all"
                >
                  <div className="w-11 h-11 rounded-xl bg-electric/10 grid place-items-center text-electric group-hover:bg-electric group-hover:text-white transition-colors shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-bold text-coal">E-mail</div>
                    <div className="text-sm text-coal/55">info@uppit.nl — antwoord binnen 24u</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-coal/40 group-hover:text-electric group-hover:translate-x-0.5 transition-all" />
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <Assurances
                testid="contact-assurances"
                className="mt-8"
                items={[
                  { icon: Star, text: "4.8/5 op Google" },
                  { icon: CalendarX, text: "Maandelijks opzegbaar" },
                  { icon: BadgeCheck, text: "Geen setup-kosten" },
                  { icon: Clock, text: "Reactie binnen 24u" },
                ]}
              />
            </Reveal>
          </div>

          {/* Right: form card */}
          <Reveal delay={0.2} className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-[1.75rem] border border-ivory-400 bg-white p-6 lg:p-9 shadow-card-light"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Naam *" id="name" value={form.name} onChange={update("name")} placeholder="Jouw naam" testid="contact-input-name" />
                <Field label="E-mail *" id="email" type="email" value={form.email} onChange={update("email")} placeholder="jij@bedrijf.nl" testid="contact-input-email" />
                <Field label="Telefoon" id="phone" type="tel" value={form.phone} onChange={update("phone")} placeholder="06 12 34 56 78" testid="contact-input-phone" />
                <Field label="Bedrijf" id="company" value={form.company} onChange={update("company")} placeholder="Bedrijfsnaam" testid="contact-input-company" />
              </div>

              <div className="mt-5">
                <label htmlFor="interest" className="block text-sm font-medium text-coal/70 mb-2">Waar kunnen we mee helpen?</label>
                <div className="relative">
                  <select
                    id="interest"
                    value={form.interest}
                    onChange={update("interest")}
                    data-testid="contact-input-interest"
                    className="w-full appearance-none rounded-xl border border-ivory-400 bg-ivory-100 px-4 py-3 pr-10 text-coal focus:border-electric focus:ring-2 focus:ring-electric/20 focus:outline-none transition-all cursor-pointer"
                  >
                    <option value="">Kies een dienst (optioneel)</option>
                    {services.map((s) => (
                      <option key={s.slug} value={s.title}>{s.title}</option>
                    ))}
                    <option value="Nog niet zeker">Nog niet zeker</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-coal/45" />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="message" className="block text-sm font-medium text-coal/70 mb-2">Je bericht *</label>
                <textarea
                  id="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Waar loop je tegenaan? Wat wil je bereiken?"
                  className="w-full rounded-xl border border-ivory-400 bg-ivory-100 px-4 py-3 text-coal placeholder:text-coal/35 focus:border-electric focus:ring-2 focus:ring-electric/20 focus:outline-none transition-all resize-none leading-relaxed"
                />
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-xs text-coal/45 max-w-xs">
                  Persoonlijk antwoord van Tim — geen automation, geen spam.
                </p>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={status === "loading"}
                  className="group inline-flex items-center justify-center gap-2.5 bg-electric hover:bg-electric-600 disabled:opacity-60 text-white rounded-full px-8 py-3.5 font-semibold text-sm transition-all duration-300 hover:shadow-glow-blue w-full sm:w-auto"
                >
                  {status === "loading" ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Versturen…</>
                  ) : status === "success" ? (
                    <><CheckCircle2 className="w-4 h-4" /> Verzonden</>
                  ) : (
                    <>Verstuur bericht <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, id, value, onChange, placeholder, type = "text", testid }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-coal/70 mb-2">{label}</label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full rounded-xl border border-ivory-400 bg-ivory-100 px-4 py-3 text-coal placeholder:text-coal/35 focus:border-electric focus:ring-2 focus:ring-electric/20 focus:outline-none transition-all"
    />
  </div>
);

export default Contact;
